export interface FaultLike {
  readonly status?: unknown;
  readonly message?: unknown;
  readonly detail?: unknown;
  readonly refusal?: unknown;
}

export interface DoorFaultEvidence {
  readonly door: string;
  readonly status: number | null;
  readonly message: string;
  readonly detail: string | null;
  readonly label: string;
  readonly retained: string | null;
}

export interface ServedVerdict {
  readonly verdict?: string | null;
}

/** A closure exists only after a door answered with the served CLOSED verdict. */
export function servedClosure<T extends ServedVerdict>(state: T | null | undefined): T | null {
  return state?.verdict === "CLOSED" ? state : null;
}

/** Age retained data from that data's timestamp (or the successful fetch clock supplied by its owner). */
export function retainedAgeLabel(dataClockMs: number | null, nowMs = Date.now()): string | null {
  if (dataClockMs === null || !Number.isFinite(dataClockMs)) return null;
  const ageS = Math.max(0, Math.floor((nowMs - dataClockMs) / 1_000));
  return `RETAINED · age ${ageS}s`;
}

function readFault(error: unknown): { status: number | null; message: string; detail: string | null; refusal: boolean } {
  const shaped = error && typeof error === "object" ? error as FaultLike : null;
  const message = typeof shaped?.message === "string" ? shaped.message : String(error);
  const explicit = typeof shaped?.status === "number" && Number.isFinite(shaped.status) && shaped.status >= 400
    ? Math.trunc(shaped.status)
    : null;
  const embedded = /\bHTTP\s+(\d{3})\b/i.exec(message);
  return {
    status: explicit ?? (embedded ? Number(embedded[1]) : null),
    message,
    detail: typeof shaped?.detail === "string" && shaped.detail.length > 0 ? shaped.detail : null,
    refusal: shaped?.refusal === true,
  };
}

export function doorFailureEvidence(
  door: string,
  error: unknown,
  retainedClockMs: number | null = null,
  nowMs = Date.now(),
): DoorFaultEvidence {
  const fault = readFault(error);
  const statusLabel = fault.refusal ? "REFUSAL" : fault.status === null ? "TRANSPORT" : `HTTP ${fault.status}`;
  const detail = fault.detail === null ? "" : ` — ${fault.detail}`;
  return {
    door,
    status: fault.status,
    message: fault.message,
    detail: fault.detail,
    label: `${door} · ${statusLabel} — ${fault.message}${detail}`,
    retained: retainedAgeLabel(retainedClockMs, nowMs),
  };
}

/** The one logging edge: one call in, exactly one console.error out. */
export function reportDoorFailure(
  door: string,
  error: unknown,
  retainedClockMs: number | null = null,
  nowMs = Date.now(),
  log: (line: string) => void = console.error,
): DoorFaultEvidence {
  const evidence = doorFailureEvidence(door, error, retainedClockMs, nowMs);
  log(`DOOR FAILURE · ${evidence.label}`);
  return evidence;
}
